/* © CNES */
import { Divider } from 'primereact/divider';
import { ScrollPanel } from 'primereact/scrollpanel';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGetItems } from '../../../../api/items/UseItemsQuery';
import { allVariableOptions } from '../../../../assets/config/filters/PostelReferenciel';
import {
  filterSections,
  Missions,
  refMissions,
} from '../../../../assets/config/filters/Referenciel';
import { ConfigContext } from '../../../../context/ConfigContext';
import { FilterDataContext } from '../../../../context/FilterDataContext';
import { CollectionsForUser } from '../../../../models/CollectionsForUser';
import { subMissionInitValues } from '../../../../models/constants/Filter';
import { SelectButtonItem } from '../../../../models/SelectButtonItem';
import { DATASET, INSTRUMENT, PPARAMETER } from '../../../shared/Constants';
import useUrlUpdater from '../../../shared/hooks/useUrlUpdater';
import {
  filterSubSectionsOfMission,
  getDefaultSelectedOption,
  getOptionsForSubSections,
  parseFilterContextToUrl,
  parseUrlToContextFilter,
  showCloudCover,
  transformFilterData,
} from '../../../shared/Utils';
import { DataProductCondition } from './dataProductCondition/DataProductCondition';
import { FilterDynamicList } from './FilterDynamicList/FilterDynamicList';
import { FilterManageButtons } from './FilterManageButtons/FilterManageButtons';
import './Filters.scss';
import { MissionCondition } from './missionCondition/MissionCondition';
import { PlatformCondition } from './platformCondition/PlatformCondition';
import { ProductTypeCondition } from './productTypeCondition/ProductTypeCondition';
import { SensorModeCondition } from './sensorMode/SensorModeCondition';
import { SliderCloud } from './SliderCloud/SliderCloud';
import { VariableCondition } from './variableCondition/VariableCondition';
import { WhenCondition } from './WhenCondition/WhenCondition';
import { WhereCondition } from './whereCondition/WhereCondition';

/**
 * Filters Props.
 */
export interface FiltersProps {
  setIsFetching: (value: boolean) => void;
  setFiltersVisibility: Function;
  userCollections: CollectionsForUser;
}

/**
 * SelectedMissionsType
 */
type SelectedMissionsType = string | number[] | null | undefined;
/**
 * SelectedProductType
 */
type SelectedProductType = string | number[] | null | undefined;

/**
 * Filters displays the full filter options to filter regards items from.
 * @returns Filters Element.
 */
export const Filters = ({
  setIsFetching,
  setFiltersVisibility,
  userCollections,
}: FiltersProps) => {
  const context = useContext(ConfigContext);
  const { setFilterData, filterData } = useContext(FilterDataContext);
  const [selectedMissions, setSelectedMissions] =
    useState<SelectedMissionsType>(
      getDefaultSelectedOption(filterData[DATASET], refMissions)
    );
  const [selectedPostelProductType, setSelectedPostelProductType] =
    useState<SelectedProductType>(
      getDefaultSelectedOption(filterData[PPARAMETER], allVariableOptions)
    );
  const [dataProductOptions, setDataProductOptions] = useState<
    SelectButtonItem[] | null
  >(null);
  const [platformOptions, setPlatformOptions] = useState<
    SelectButtonItem[] | null
  >(null);
  const [sensorModeOptions, setSensorModeOptions] = useState<
    SelectButtonItem[] | null
  >(null);
  const [variableOptions, setVariableOptions] = useState<
    SelectButtonItem[] | null
  >(null);
  const [isCloudCoverVisible, setIsCloudCoverVisible] =
    useState<boolean>(false);
  const [isPostelSectionsVisible, setIsPostelSectionsVisible] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isFetching, data } = useGetItems(
    {
      params: filterData,
      token: context.nextToken,
      intersects: context.searchZone.value,
      bbox:
        context.drawnShape ??
        (context.mapExtent.currentView ? context.mapExtent.extent : undefined),
    },
    navigate,
    context.setError400
  );

  /**
   * Get params from URL on component first render
   */
  useEffect(() => {
    setFiltersVisibility(true);
    const params: { key: string; value: string }[] = [];
    searchParams.forEach((value, key) => {
      params.push({ key, value });
    });
    if (params.length) {
      const {
        contextFilter,
        selectedMissions,
        selectedPostelProductType,
        drawnShape,
      } = parseUrlToContextFilter(params);

      setSelectedMissions(selectedMissions);
      setSelectedPostelProductType(selectedPostelProductType);

      setFilterData({
        ...contextFilter,
        ...(selectedMissions?.includes(Missions.GEOV2_AVHRR) && {
          [INSTRUMENT]: ['AVHRR'],
        }),
      });
      setTimeout(() => {
        context.setDrawnShape(drawnShape);
      }, 0);
    }
    // eslint-disable-next-line
  }, []);

  /**
   * setIsFetching when isFetching changes.
   */
  useEffect(() => {
    setIsFetching(isFetching);
  }, [isFetching, setIsFetching]);

  /**
   * set searchedFeatures and featuresCount when data changes.
   */
  useEffect(() => {
    if (filterData[DATASET] && data) {
      context.setSearchedFeatures(data?.features, data?.numberMatched || 0);
    } else {
      context.setSearchedFeatures([], data?.numberMatched || 0);
    }
    // eslint-disable-next-line
  }, [data, filterData[DATASET]]);

  /**
   * filter values of platform, product type and sensor mode options on mission values change
   * if all mission values are selected then set all options
   */
  useEffect(() => {
    const {
      filteredPlatformOptions,
      filteredProductTypesOptions,
      filteredSensorModeOptions,
    } = getOptionsForSubSections(selectedMissions);

    handleShowHidePostelSections(selectedMissions);
    setPlatformOptions(filteredPlatformOptions);
    setDataProductOptions(filteredProductTypesOptions);
    setSensorModeOptions(filteredSensorModeOptions);
    // eslint-disable-next-line
  }, [selectedMissions]);

  useEffect(() => {
    if (showCloudCover(selectedMissions, filterData)) {
      setIsCloudCoverVisible(true);
    } else {
      setIsCloudCoverVisible(false);
    }
  }, [selectedMissions, filterData]);

  // update options for Variable section on selected product type changed
  useEffect(() => {
    let filteredVariableOptions: SelectButtonItem[] | null =
      selectedPostelProductType === 'All' ? allVariableOptions : null;
    if (Array.isArray(selectedPostelProductType)) {
      filteredVariableOptions = allVariableOptions.filter((variableItem) => {
        if (Array.isArray(variableItem.productTypeId)) {
          return variableItem.productTypeId.some((id) =>
            selectedPostelProductType.includes(id)
          );
        } else {
          return selectedPostelProductType.includes(variableItem.productTypeId);
        }
      });
    }
    setVariableOptions(filteredVariableOptions);
  }, [selectedPostelProductType]);

  /**
   * show or hide sections when collection postel selected
   * @param missions list of missions values
   * @returns
   */
  const handleShowHidePostelSections = useCallback(
    (missions: string | number[] | null | undefined) => {
      if (
        missions instanceof Array &&
        (missions.includes(Missions.postel) ||
          missions.includes(Missions.GEOV2_AVHRR))
      ) {
        setIsPostelSectionsVisible(true);
      } else {
        setIsPostelSectionsVisible(false);
      }
      setVariableOptions(null);
    },
    []
  );

  // Updates the URL parameters based on changes in the map extent, drawn shapes, or filter data
  useUrlUpdater();

  /**
   * handleChange on Condition components.
   */
  const handleChange = useCallback(
    (value: string | number[], key: string) => {
      context.setQueryPage(1);
      let newFilterValue = null;
      if (key === filterSections.mission.param) {
        setSelectedMissions(value);
        if (value?.length > 0) {
          newFilterValue = {
            ...filterSubSectionsOfMission(value, filterData),
            ...transformFilterData(key, value, filterData),
          };
        } else {
          newFilterValue = {
            ...filterSubSectionsOfMission(value, filterData),
            ...subMissionInitValues,
          };
        }
      } else if (key === filterSections.productType.param) {
        setSelectedPostelProductType(value);
        newFilterValue = transformFilterData(key, value, filterData);
      } else {
        newFilterValue = transformFilterData(key, value, filterData);
      }
      setFilterData(newFilterValue);
      const urlParams = parseFilterContextToUrl(
        { ...filterData, ...newFilterValue },
        context.drawnShape,
        context.mapExtent.currentView ? context.mapExtent.extent : undefined
      );

      navigate({
        pathname: urlParams ? '/search' : '/',
        ...(urlParams && { search: urlParams }),
      });
    },
    // eslint-disable-next-line
    [setFilterData, navigate, context.mapExtent, filterData, context.drawnShape]
  );

  return (
    <ScrollPanel
      style={{
        width: '100%',
        height: '100%',
      }}
    >
      <div className="filterTab">
        <FilterManageButtons setSelectedMissions={setSelectedMissions} />
        <FilterDynamicList setSelectedMissions={setSelectedMissions} />
        <Divider />
        <WhenCondition handleChange={handleChange} />
        <WhereCondition handleChange={handleChange} />
        <MissionCondition
          handleChange={handleChange}
          userCollections={userCollections}
        />

        {isPostelSectionsVisible ? (
          <>
            <ProductTypeCondition handleChange={handleChange} />
            <VariableCondition
              handleChange={handleChange}
              variableOptions={variableOptions}
            />
          </>
        ) : (
          <div>
            {platformOptions?.length !== 0 &&
              platformOptions?.length !== undefined && (
                <PlatformCondition
                  handleChange={handleChange}
                  platformOptions={platformOptions}
                />
              )}
            {dataProductOptions?.length !== 0 &&
              platformOptions?.length !== undefined && (
                <DataProductCondition
                  handleChange={handleChange}
                  dataProductOptions={dataProductOptions}
                />
              )}
            {sensorModeOptions?.length !== 0 &&
              sensorModeOptions?.length !== undefined && (
                <SensorModeCondition
                  handleChange={handleChange}
                  sensorModeOptions={sensorModeOptions}
                />
              )}
            {isCloudCoverVisible && <SliderCloud handleChange={handleChange} />}
          </div>
        )}
      </div>
    </ScrollPanel>
  );
};
